import { ApolloQueryResult } from '@apollo/client';
import toNumber from 'lodash/toNumber';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Lozenge from '@atlaskit/lozenge';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import {
  EntitlementIcon,
  entitlementUtils,
  ErrorComponents,
  InfoPanel,
  InvoiceGroupSelectItem,
  NotificationTypes,
  SelectPromotionSelectItem,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import {
  currentEntitlementType,
  getLinkedEntitlementsDetails,
  getParentContainerId,
} from '@css-xp/base/entitlement-utils';
import { withApollo } from '@css-xp/base/graphql-provider';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  TransactionAccount,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { UpdateOrderRequest, CustomOrderEntitlement } from '@atlassian/cssxp-typescommon';
import { isFreePlanEntitlement, constructCustomOrderRequest } from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_PARTNER_PROMOTION,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
  ENABLE_OMS_SERVICE_V2,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import classNames from 'classnames';
import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import defaultTo from 'lodash/defaultTo';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import WarningDialog from '../../warning-dialog';
import { ConfirmTransfer } from './confirm';
import ManageBillingProfile from './manage-billing-profile';
import { ReviewAndSubmitChange } from './review-change';
import { SearchPage } from './select-transaction-account';

import { isTrialEntitlement } from '../../../utils';
import {
  getIntendedBillingPeriodState,
  getPartnerPromotionQuery,
} from '../../manage-entitlement/src/manage-entitlement-helper';

import './transfer-entitlement.scss';

enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

enum FormTrackerStep {
  SEARCH_ACCOUNT = 0,
  BILLING_PROFILE,
  CONFIRM,
  REVIEW_AND_SUBMIT,
}

enum FormStages {
  SEARCH_ACCOUNT = 'Billing account',
  BILLING_PROFILE = 'Billing profile',
  CONFIRM = 'Confirm',
  REVIEW_AND_SUBMIT = 'Review and submit',
}

interface CustomValues {
  value: string;
  label: string;
}

interface Props {
  entitlement: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  transactionAccount: TransactionAccount;
  invoiceGroup: InvoiceGroup;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export interface FormData {
  currentPlan?: string;
  newPlan?: {
    label: string;
    value: string;
  };
  units?: string;
  billingFrequency?: BillingFrequency;
  pricing?: string;
  promotionsToAdd?: SelectPromotionSelectItem[];
  promotionsToRemove?: SelectPromotionSelectItem[];
  invoiceGroupId?: InvoiceGroupSelectItem;
  supportTicketLink?: string;
  termsOfTransfer?: boolean;
  notes?: string;
  customDuration?: CustomValues[];
  customIterations?: string[];
  customReason?: string;
  customValue?: string;
  selectedBillingAccount?: TransactionAccount;
  selectedBillingProfile?: InvoiceGroup;
  searchQuery?: string;
  currentBillingAdmin?: string | null;
  newBillingAdmin?: string | null;
  partnerPromotion?: Promotion;
}

export enum OrderType {
  ACCOUNT_MODIFICATION_ORDER = 'ACCOUNT_MODIFICATION_ORDER',
}

interface SearchSubmitEvent {
  [key: string]: any;
  query?: string;
}

export type PartnerPromotionType = {
  error?: any;
  loading?: boolean;
  response?: Promotion | null | undefined;
};

export const SubscriptionTxATransfer = ({ entitlement, transactionAccount, invoiceGroup, refetch }: Props) => {
  const [currentProgressIndex, setCurrentProgressIndex] = useState<number>(0);
  const [currFormState, setCurrFormState] = useState<FormData>({});
  const [isApprovalRequired, setIsApprovalRequired] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [linkedEntitlementsData, setLinkedEntitlementsData] = useState<Array<LinkedDestinationEntitlement>>([]);
  const [isEnabled] = useFeatureToggle();
  const isEntRelationFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);
  const isPartnerPromotionEnabled = isEnabled(ENABLE_PARTNER_PROMOTION);
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);
  const [partnerPromotionData, setPartnerPromotionData] = useState<PartnerPromotionType>({});
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
  });
  const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = useState(false);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const { closeSidebar } = useSidebar();

  const linkedEntsTableVisible = useMemo(() => {
    if (isEntRelationFeatureEnable) {
      if (isStatuspage(entitlement?.productId)) {
        return true;
      }
      return !!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length;
    }
  }, [entitlement, isEntRelationFeatureEnable]);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  const buildRequest = () => {
    let request: UpdateOrderRequest = {
      entitlementId: entitlement.id,
      transferTransactionAccountId: defaultTo(currFormState.selectedBillingAccount?.id, ''),
      orderType: OrderType.ACCOUNT_MODIFICATION_ORDER,
      productId: entitlement?.productId ?? '',
    };
    if (currFormState?.selectedBillingProfile?.id) {
      request.transferInvoiceGroupId = currFormState?.selectedBillingProfile?.id;
    }
    let invoiceGroupId;
    if (isFreePlanEntitlement(entitlement)) {
      const linkedPaidEntitlement = linkedEntitlementsWithBillingGroup();
      if (linkedPaidEntitlement.length > 0) {
        invoiceGroupId = linkedPaidEntitlement[0].billingGroupId as string;
        request.invoiceGroupId = invoiceGroupId;
      }
    }
    if (isEntRelationFeatureEnable) {
      request.linkedEntitlements = linkedEntitlementsData.length
        ? getLinkedEntitlementsDetails(
            entitlement,
            linkedEntitlementsData.filter(
              (linkedEntitlementObj) => linkedEntitlementObj.billingStatus !== BillingStatus.INACTIVE
            )
          )
        : [];
    }

    const metadata = {
      [OMSMetadata.SUPPORT_TICKET_NUMBER]: defaultTo(currFormState?.supportTicketLink, null),
      [OMSMetadata.HAND_NOTE]: defaultTo(currFormState?.notes, null),
      [OMSMetadata.TERM_TRANSFER]: defaultTo(currFormState?.termsOfTransfer?.toString(), null),
      [OMSMetadata.CURRENT_BILLING_ADMIN]: defaultTo(currFormState?.currentBillingAdmin, null),
      [OMSMetadata.NEW_BILLING_ADMIN]: defaultTo(currFormState?.newBillingAdmin, null),
      [OMSMetadata.REASON_CODE]: defaultTo(currFormState?.customReason, null),
    };

    request = {
      ...request,
      metadata: metadata,
    };

    if (isOmsServiceV2Enabled) {
      const entitlementsList: CustomOrderEntitlement[] = [
        {
          entitlementId: entitlement.id,
          productId: entitlement?.productId as string,
          transferTransactionAccountId: defaultTo(currFormState.selectedBillingAccount?.id, ''),
          transferInvoiceGroupId: currFormState?.selectedBillingProfile?.id
            ? currFormState.selectedBillingProfile.id
            : undefined,
        },
      ];

      const linkedEntitlements = linkedEntitlementsData?.filter(
        (linkedEntitlementObj) => linkedEntitlementObj.billingStatus !== BillingStatus.INACTIVE
      );
      linkedEntitlements?.forEach((linkedEntitlement) => {
        entitlementsList.push({
          entitlementId: linkedEntitlement.entitlementId,
          productId: linkedEntitlement.productId as string,
        });
      });

      const constructV2Request = {
        metadata: metadata,
        orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
        billingFrequency: entitlement?.billingFrequency as string,
        entitlements: entitlementsList,
        transactionAccountId: entitlement?.transactionAccountId as string,
        invoiceGroupId: invoiceGroupId,
      };
      return constructCustomOrderRequest(constructV2Request);
    }
    return request;
  };

  const successMessage = () => {
    return (
      <div>
        <Link to={`#order-history`}>View order history</Link>
      </div>
    );
  };

  const handleConfirmDialog = () => {
    setConfirmDialogOpen(false);
    const requestData = buildRequest();
    submitForm(requestData);
  };

  const handleFormSubmit = (formData: FormData) => {
    if (currentProgressIndex < items.length - 1 && currentProgressIndex === FormTrackerStep.SEARCH_ACCOUNT) {
      setCurrFormState(cloneDeep(assign(currFormState, formData)));
      setCurrentProgressIndex(currentProgressIndex);
    } else if (currentProgressIndex < items.length - 1) {
      setCurrFormState(cloneDeep(assign(currFormState, formData)));
      setCurrentProgressIndex(currentProgressIndex + 1);
    } else {
      if (warningModalDailogVisible) {
        setConfirmDialogOpen(true);
      } else {
        const request = buildRequest();
        submitForm(request);
      }
    }
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('subscription-transaction-account-transfer')
      .withHeader('Task complete')
      .withMessage(successMessage())
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  const stages = Object.values(FormStages);

  const items: Stages = stages.map((stage, index) => ({
    id: `${index}`,
    label: stage,
    percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
    status:
      currentProgressIndex > index
        ? TrackerState.VISITED
        : currentProgressIndex === index
        ? TrackerState.CURRENT
        : TrackerState.UNVISITED,
    onClick: () => setCurrentProgressIndex(index),
  }));

  const selectBillingProfile = (billingProfile: InvoiceGroup) => {
    const formState = { ...currFormState };
    formState.selectedBillingProfile = billingProfile;
    setCurrFormState(formState);
    setCurrentProgressIndex(currentProgressIndex + 1);
  };

  const linkedEntitlementsWithBillingGroup = () => {
    const paidEntitlement =
      linkedEntitlementsData &&
      linkedEntitlementsData.filter((entitlement: LinkedDestinationEntitlement) => entitlement.billingGroupId);
    return paidEntitlement || [];
  };

  const isStatuspageProduct = isStatuspage(entitlement?.productId);
  const isCurrentEntitlementOnTrial = isTrialEntitlement(entitlement, isStatuspageProduct);

  const setPartnerPromotion = async (billingAccount: TransactionAccount) => {
    if (isPartnerPromotionEnabled && !isFreePlanEntitlement(entitlement)) {
      const intendedBillingPeriodState = getIntendedBillingPeriodState(!isCurrentEntitlementOnTrial);
      try {
        setPartnerPromotionData({
          error: null,
          loading: true,
        });
        const partnerPromotionResponse = await getPartnerPromotionQuery({
          entitlementNumber: entitlement.entitlementNumber,
          intendedBillingPeriodState,
          discountTier: billingAccount?.partnerDiscountTier,
          programLevelGlobal: billingAccount?.programLevelGlobal,
        });
        const partnerPromotion = partnerPromotionResponse?.data?.entitlementDetails?.availablePartnerPromotions?.[0];
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          response: partnerPromotion ?? null,
        }));
      } catch (error) {
        // TODO: handle error case
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          error,
        }));
      } finally {
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          loading: false,
        }));
      }
    }
  };

  const selectBillingAccount = (billingAccount: TransactionAccount) => {
    const formState = { ...currFormState };

    //Reset the billing admins fields if different billing account selected
    const billingAcc = billingAccount.transactionAccountNumber;
    const selectedBillingAcc = formState.selectedBillingAccount?.transactionAccountNumber;
    if (selectedBillingAcc !== billingAcc) {
      delete formState.termsOfTransfer;
      delete formState.currentBillingAdmin;
      delete formState.newBillingAdmin;
    }

    formState.selectedBillingAccount = billingAccount;
    setPartnerPromotion(billingAccount);
    setCurrFormState(formState);
    const linkPaidEntitlements = linkedEntitlementsWithBillingGroup();
    if (isFreePlanEntitlement(entitlement) && linkPaidEntitlements.length === 0) {
      setCurrentProgressIndex(currentProgressIndex + 2);
    } else {
      setCurrentProgressIndex(currentProgressIndex + 1);
    }
  };

  const changeBillingAccount = () => {
    const formState = { ...currFormState };
    delete formState.selectedBillingAccount;
    setCurrFormState(formState);
    setCurrentProgressIndex(currentProgressIndex - 1);
  };

  const renderFormStep = () => {
    switch (currentProgressIndex) {
      case FormTrackerStep.SEARCH_ACCOUNT: {
        return (
          <SearchPage
            transactionAccountNumber={transactionAccount?.transactionAccountNumber}
            formState={currFormState}
            selectBillingAccount={selectBillingAccount}
          />
        );
      }
      case FormTrackerStep.BILLING_PROFILE:
        return (
          <ManageBillingProfile
            formState={currFormState}
            selectBillingProfile={selectBillingProfile}
            changeBillingAccount={changeBillingAccount}
            billingFrequency={entitlement?.billingFrequency}
            endTimestamp={entitlement?.endTimestamp}
          />
        );
      case FormTrackerStep.CONFIRM:
        return (
          <ConfirmTransfer
            formState={currFormState}
            transactionAccount={transactionAccount}
            setCurrFormState={setCurrFormState}
            setDisabled={setIsDisabled}
            changeBillingAccount={changeBillingAccount}
            partnerPromotionData={partnerPromotionData}
            entitlement={entitlement}
          />
        );
      case FormTrackerStep.REVIEW_AND_SUBMIT: {
        return (
          <ReviewAndSubmitChange
            previousTransactionAccount={transactionAccount}
            previousInvoiceGroup={invoiceGroup}
            previousBillingAdmin={currFormState?.currentBillingAdmin}
            newTransactionAccount={currFormState?.selectedBillingAccount}
            newInvoiceGroup={currFormState?.selectedBillingProfile}
            newBillingAdmin={currFormState?.newBillingAdmin}
            newPartnerDiscount={currFormState?.partnerPromotion}
            isFinalStep={true}
          />
        );
      }
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <form {...formProps}>
          <div className="header">
            <FormHeader title="Transfer subscription to another billing account">
              <EntitlementIcon
                logoId={entitlement?.logoId as string}
                productId={entitlement?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <h4 style={{ display: 'inline' }}>{`${defaultTo(entitlement?.productName, 'Unnamed product')} ${
                entitlement?.entitlementNumber
              }`}</h4>
              &nbsp;&nbsp;
              {entitlement?.scheduledChanges !== null && <Lozenge appearance="inprogress">changes scheduled</Lozenge>}
              {!!entitlement?.cloudSiteUrl && (
                <div>
                  <Field name="siteName" id="siteName" label="Site Name" defaultValue={entitlement?.cloudSiteUrl}>
                    {() => <p>{entitlement?.cloudSiteUrl}</p>}
                  </Field>
                </div>
              )}
            </FormHeader>
          </div>
          {linkedEntsTableVisible && (
            <>
              <FormSection>
                <entitlementUtils.LinkedEntitlementsTable
                  message={'This action will also change the billing account for these subscriptions:'}
                  entitlementId={entitlement.id ?? ''}
                  parentContainerID={getParentContainerId(entitlement)}
                  productType={entitlement.productType}
                  currentEntitlementType={currentEntitlementType(entitlement)}
                  destinationEntitlementType={[
                    LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
                    LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
                    LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
                    LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
                  ]}
                  findApps={true}
                  findProducts={true}
                  updateLinkedEntitlements={setLinkedEntitlementsData}
                  setLoading={setLoadingLinkedEntitlements}
                />
              </FormSection>
            </>
          )}
          <div className="progress-content">
            <ProgressTracker items={items} spacing="compact" />
          </div>
          <FormSection>
            {formError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to transfer transaction account for the following reason"
                    message={formError}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>{renderFormStep()}</FormSection>
          {(currentProgressIndex === FormTrackerStep.CONFIRM ||
            currentProgressIndex === FormTrackerStep.REVIEW_AND_SUBMIT) && (
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Cancel
                </Button>

                <LoadingButton
                  className={classNames({ 'button approval': isApprovalRequired })}
                  appearance="primary"
                  type="submit"
                  isDisabled={isDisabled || loadingLinkedEntitlement || partnerPromotionData?.loading}
                  isLoading={isProcessing || partnerPromotionData?.loading}
                  testId="save-button"
                >
                  {currentProgressIndex === FormTrackerStep.CONFIRM ? 'Confirm and continue' : 'Transfer'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          )}
          {warningModalDailogVisible && (
            <WarningDialog
              isOpen={isConfirmDialogOpen}
              onClose={() => setConfirmDialogOpen(false)}
              onConfirm={() => handleConfirmDialog()}
              header={'Confirm transfer'}
              description={
                <p>
                  This action will also change the billing account for other product and app subscriptions on this site
                  that belong to the same product family.
                </p>
              }
              okLabel={'Confirm'}
              testId={'switch-txa-submission'}
            />
          )}
        </form>
      )}
    </Form>
  );
};

export default withApollo(SubscriptionTxATransfer);
